import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import DatePicker from "react-datepicker";
import TableChartIcon from "@mui/icons-material/TableChart";
import { useNavigate } from "react-router-dom";
import Chart from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { Document, Packer, Paragraph, Table, TableCell, TableRow, TextRun } from "docx";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useDispatch, useSelector } from "react-redux";
import { fetchGroups } from "../../../../../store/slices/groupSlice";
import {
  setProbabilityToDateRedux, setProbabilityFromDateRedux,
  setProbabilityGroupRedux,
} from "../../../../../store/slices/filterSlice";
import { setFilteredNavData } from "../../../../../store/slices/LeadreportfilteredSlice";
import RemoveIcon from '@mui/icons-material/Remove';
import { useCurrencySymbol } from "../../../../../utils/currencyUtils";
import DateConversion from "../../../../../utils/DateConversion";
import CommaConvert from "../../../../../utils/CommaConvert";


const ProbabilityStatusL1 = ({ wrapperClasses }) => {
  const dispatch = useDispatch();
    const currency = useCurrencySymbol();
  const { formatDateByCountry, getDatePlaceholder, getDateFormat } = DateConversion();
    const USAComma = CommaConvert();
    
  const [probabilityDownload, setProbabilityDownload] = useState([]);
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);

  const [filteredLeadReportsLead, setFilteredLeadReportsLead] = useState([]);

  const { filteredReports } = useSelector((state) => state.filteredData);
  const { group } = useSelector((state) => state.group);
  const groupNameById = useMemo(() => {
    const normalize = (value) => String(value ?? "").trim();
    const map = new Map();
    (Array.isArray(group) ? group : []).forEach((groupItem) => {
      const idA = normalize(groupItem?._id);
      const idB = normalize(groupItem?.id);
      const name = normalize(
        groupItem?.name ??
          groupItem?.newGroupName ??
          groupItem?.groupName ??
          groupItem?.GroupName ??
          ""
      );
      const label = name || idA || idB;
      if (idA) map.set(idA, label || idA);
      if (idB && idB !== idA) map.set(idB, label || idB);
    });
    return map;
  }, [group]);
  const groupIdByName = useMemo(() => {
    const map = new Map();
    groupNameById.forEach((name, id) => map.set(String(name).trim(), id));
    return map;
  }, [groupNameById]);
  const getLeadGroupId = useCallback((lead) => {
    const normalize = (value) => String(value ?? "").trim();
    const fromObject = normalize(lead?.groupid?._id ?? lead?.groupid?.id);
    if (fromObject) return fromObject;
    const fromString = typeof lead?.groupid === "string" ? normalize(lead.groupid) : "";
    if (fromString && groupNameById.has(fromString)) return fromString;
    if (fromString && groupIdByName.has(fromString)) return groupIdByName.get(fromString);
    const fromAssigned = normalize(lead?.AssignedGroup);
    if (fromAssigned && groupIdByName.has(fromAssigned)) return groupIdByName.get(fromAssigned);
    return "";
  }, [groupNameById, groupIdByName]);
  const getLeadGroupName = useCallback((lead) => {
    const gid = getLeadGroupId(lead);
    if (gid && groupNameById.has(gid)) return groupNameById.get(gid);
    return String(lead?.AssignedGroup || "").trim();
  }, [getLeadGroupId, groupNameById]);
  const [showgraph, setShowgraph] = useState(true);

  const handleToggleTable = () => {
    setShowgraph(!showgraph);
  };
  useEffect(() => {
    if (Array.isArray(filteredReports)) {
      const pendingLeads = filteredReports.filter(
        (lead) => lead.Status === "Pending"
      );
      setFilteredLeadReportsLead(pendingLeads);
    }
  }, [filteredReports]);

  const dropdownRefGroup = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRefGroup.current &&
        !dropdownRefGroup.current.contains(event.target)
      ) {
        setShowGroupDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleDownload = (event) => {
    const value = event.target.value;
    if (value === "pdf") {
      downloadPDF();
    } else if (value === "word") {
      downloadWord();
    } else if (value === "excel") {
      downloadExcel();
    }
    setIsDropdownVisible(false);
  };

  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  const toggleDropdown = () => {
    setIsDropdownVisible(!isDropdownVisible); // Toggle dropdown visibility
  };

  const downloadExcel = () => {
    const sortedLeads = [...probabilityDownload].sort(
      (a, b) => new Date(a.LeadDate) - new Date(b.LeadDate)
    );

    const ws = XLSX.utils.json_to_sheet(
      sortedLeads.map((LeadReport, index) => ({
        "Sr. No": index + 1,
        "Lead Date": LeadReport.LeadDate
          ? formatDateByCountry(LeadReport.LeadDate)
          : "",
        "Client Name": LeadReport.ClientName,
        "Project Name": LeadReport.ProjectName,
        "Lead Type": LeadReport.LeadType,
        "Quoted Value": USAComma(LeadReport.QuotedValue), // ✅ Ensure it's a number
        Probability: LeadReport.Probability,
        Status: LeadReport.Status,
        Group: getLeadGroupName(LeadReport),
        "Follow-up Date":
          LeadReport.Status === "Won" || LeadReport.Status === "Lost"
            ? ""
            : formatDateByCountry(LeadReport.FollowupDate),
      }))
    );

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Lead Reports");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const excelFile = new Blob([excelBuffer], {
      bookType: "xlsx",
      type: "application/octet-stream",
    });
    saveAs(excelFile, "LeadReports.xlsx");
  };

  const downloadPDF = () => {
    const doc = new jsPDF();
    doc.autoTable({
      head: [
        [
          "Sr. No",
          "Lead Date",
          "Client Name",
          "Project Name",
          "Lead Type",
          "Quoted Value",
          "Status",
          "Probability",
          "Group",
          "Follow-up Date",
        ],
      ],
      body: probabilityDownload.map((LeadReport, index) => [
        index + 1,
        LeadReport.LeadDate ? formatDateByCountry(LeadReport.LeadDate) : " ",
        LeadReport.ClientName,
        LeadReport.ProjectName,
        LeadReport.LeadType,
        `${currency}${USAComma(LeadReport.QuotedValue)}`,
        LeadReport.Status,
        LeadReport.Probability,
        getLeadGroupName(LeadReport),
        LeadReport.Status === "Won" || LeadReport.Status === "Lost"
          ? " "
          : formatDateByCountry(LeadReport.FollowupDate),
      ]),
      styles: {
        fontSize: 7, // 🔹 Reduce font size (default is 10)
      },
      headStyles: {
        fontSize: 8, // 🔹 Reduce font size for the table header
      },
    });

    doc.save("LeadReports.pdf");
  };

  const downloadWord = () => {
    const safeText = (value) => (value === null || value === undefined ? " " : String(value));
    const docxParagraphFromText = (value) =>
      new Paragraph({
        children: [new TextRun(safeText(value))],
      });
    const leadRows = (probabilityDownload || []).filter((row) => row != null);

    const headerRow = new TableRow({
      children: [
        new TableCell({
          children: [docxParagraphFromText("Sr.No")],
        }),
        new TableCell({
          children: [docxParagraphFromText("Lead Date")],
        }),
        new TableCell({
          children: [docxParagraphFromText("Client Name")],
        }),
        new TableCell({
          children: [docxParagraphFromText("Project Name")],
        }),
        new TableCell({
          children: [docxParagraphFromText("Lead Type")],
        }),
        new TableCell({
          children: [docxParagraphFromText("Quoted Value")],
        }),
        new TableCell({
          children: [docxParagraphFromText("Probability")],
        }),
        new TableCell({
          children: [docxParagraphFromText("Status")],
        }),
        new TableCell({
          children: [docxParagraphFromText("Group")],
        }),
        new TableCell({
          children: [docxParagraphFromText("Follow-up Date")],
        }),
      ],
    });

    const dataRows = leadRows.map((LeadReport, index) => {
      return new TableRow({
        children: [
          new TableCell({
            children: [docxParagraphFromText(`${index + 1}`)],
          }),
          new TableCell({
            children: [
              docxParagraphFromText(
                LeadReport.LeadDate ? formatDateByCountry(LeadReport.LeadDate) : " "
              ),
            ],
          }),
          new TableCell({
            children: [docxParagraphFromText(LeadReport.ClientName)],
          }),
          new TableCell({
            children: [docxParagraphFromText(LeadReport.ProjectName)],
          }),
          new TableCell({
            children: [docxParagraphFromText(LeadReport.LeadType)],
          }),
          new TableCell({
            children: [
              docxParagraphFromText(
                `${currency}${USAComma(LeadReport.QuotedValue)}`
              ),
            ],
          }),
          new TableCell({
            children: [docxParagraphFromText(LeadReport.Probability)],
          }),
          new TableCell({
            children: [docxParagraphFromText(LeadReport.Status)],
          }),

          new TableCell({
            children: [docxParagraphFromText(getLeadGroupName(LeadReport))],
          }),
          new TableCell({
            children: [
              docxParagraphFromText(
                LeadReport.Status === "Won" || LeadReport.Status === "Lost"
                  ? " "
                  : formatDateByCountry(LeadReport.FollowupDate)
              ),
            ],
          }),
        ],
      });
    });

    const doc = new Document({
      sections: [
        {
          properties: {},
          children: [
            new Table({
              rows: [headerRow, ...dataRows],
            }),
          ],
        },
      ],
    });

    Packer.toBlob(doc).then((blob) => {
      saveAs(blob, "LeadReports.docx");
    });
  };

  const popupRef = useRef(null);

  const handleClickOutside = (event) => {
    if (popupRef.current && !popupRef.current.contains(event.target)) {
      setIsDropdownVisible(false); // Close the popup if clicked outside
    }
  };
  useEffect(() => {
    if (isDropdownVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownVisible]);

  const [probabilityTab, setProbabilityTab] = useState("graph");

  const [AssignedGroup, setAssignedGroup] = useState("");
  const [probabilityStatus, setProbabilityStatus] = useState([]);

  const [probabilityFilterDateFrom, setProbabilityFilterDateFrom] = useState();
  const [probabilityFilterDateTo, setProbabilityFilterDateTo] = useState();

  const [selectedFromDate, setSelectedFromDate] = useState(null); // Temporary state
  const [selectedToDate, setSelectedToDate] = useState(null); // Temporary state

  const groupFilterRedux = useSelector((state) => state.filters.groupFilter);

  const [selectedGroup, setSelectedGroup] = useState(
    groupFilterRedux === "All" ? "" : groupFilterRedux || "");

  useEffect(() => {
    if (groupFilterRedux !== undefined && groupFilterRedux !== null) {
      const asId = groupIdByName.get(String(groupFilterRedux).trim()) || groupFilterRedux;
      setSelectedGroup(asId);
    } else { setSelectedGroup("All") }
  }, [groupFilterRedux, groupIdByName]);

  const handleProbabilityGroup = (groupId) => {
    if (!groupId) return;
    setSelectedGroup(groupId);
    if (groupId === "All") {
      // Set AssignedGroup to an empty string or a special value
      setSelectedGroup(groupIdByName.get(String(groupFilterRedux).trim()) || groupFilterRedux);
      setShowGroupDropdown(false);
      return;
    }
    setShowGroupDropdown(false);
    dispatch(setProbabilityGroupRedux(groupId));
  };

  const handleProbabilityFromDate = (date) => {
    if (!date) return;
    setSelectedFromDate(date);
    dispatch(setProbabilityFromDateRedux(date));
  };
  const handleProbabilityToDate = (date) => {
    if (!date) return;
    setSelectedToDate(date);
    dispatch(setProbabilityToDateRedux(date));
  };




  const probabilityFilterDateFromRedux = useSelector(
    (state) => state.filters.probabilityFilterDateFrom
  );
  const probabilityFilterToFromRedux = useSelector(
    (state) => state.filters.probabilityFilterToFrom
  );
  const probabilityGroupFilterRedux = useSelector(
    (state) => state.filters.probabilityGroupFilter
  );

  useEffect(() => {
    if (probabilityFilterDateFromRedux !== undefined && probabilityFilterDateFromRedux !== null) {
      setSelectedFromDate(probabilityFilterDateFromRedux);
    } else {
      setSelectedFromDate("")
    }
  }, [probabilityFilterDateFromRedux]);

  useEffect(() => {
    if (probabilityFilterToFromRedux !== undefined && probabilityFilterToFromRedux !== null) {
      setSelectedToDate(probabilityFilterToFromRedux);
    } else { setSelectedToDate("") }
  }, [probabilityFilterToFromRedux]);

  useEffect(() => {
    if (probabilityGroupFilterRedux) {
      const asId = groupIdByName.get(String(probabilityGroupFilterRedux).trim()) || probabilityGroupFilterRedux;
      setSelectedGroup(asId);
    }
  }, [probabilityGroupFilterRedux, groupIdByName]);

  const probabilityGroupDropdownLabel = useMemo(() => {
    const norm = (v) => (v === undefined || v === null ? "" : String(v).trim());
    const sel = norm(selectedGroup);
    const gf = norm(groupFilterRedux);

    if (sel === "All" || (!sel && (!gf || gf === "All"))) return "All";

    if (sel && groupNameById.has(sel)) return groupNameById.get(sel);

    const idFromDashboard =
      (gf && groupNameById.has(gf) ? gf : null) ||
      (gf ? groupIdByName.get(gf) : null) ||
      sel ||
      gf;
    if (!idFromDashboard || idFromDashboard === "All") return "All";
    if (groupNameById.has(idFromDashboard)) return groupNameById.get(idFromDashboard);

    const idFromName = groupIdByName.get(idFromDashboard);
    if (idFromName && groupNameById.has(idFromName)) return groupNameById.get(idFromName);

    return sel || gf || "All";
  }, [selectedGroup, groupFilterRedux, groupNameById, groupIdByName]);

  const [probCalculatedCount, setProbCalculatedCount] = useState(0);
  const [calculatedPercentage, setCalculatedPercentage] = useState(0);
  const [totalProbabilityAmount, setTotalProbabilityAmount] = useState(0);
  const [totalprobabilityCount, setTotalProbabilityCount] = useState(0);

  const aggregateProbabilityData = useCallback(
    (data) => {
      const probMap = new Map();
      let totalProbAmount = 0;
      const totalCount = filteredLeadReportsLead.length;
      const calculatedCount = data.length;
      setProbCalculatedCount(calculatedCount);

      data.forEach((LeadReport) => {
        const prob = LeadReport.Probability;
        if (!prob) return;

        const count = probMap.has(prob) ? probMap.get(prob).count + 1 : 1;
        const amount = parseFloat(LeadReport.QuotedValue);
        totalProbAmount += amount;

        probMap.set(prob, {
          count,
          amount: (probMap.get(prob)?.amount || 0) + amount,
        });
      });

      let potentialProbabilityArray = Array.from(
        probMap,
        ([prob, { count, amount }]) => ({
          prob,
          count,
          amount,
          percentage: (count / totalCount) * 100, // Store as a number
        })
      );

      // Calculate the total percentage
      let totalPercentage = potentialProbabilityArray.reduce(
        (sum, prob) => sum + prob.percentage,
        0
      );

      // Adjust the total percentage to 100% if counts match
      if (totalCount === calculatedCount) {
        const discrepancy = 100 - totalPercentage;
        totalPercentage = 100;

        // Adjust the largest segment to account for the discrepancy
        if (potentialProbabilityArray.length > 0) {
          const maxIndex = potentialProbabilityArray.reduce(
            (maxIdx, prob, idx, arr) =>
              prob.percentage > arr[maxIdx].percentage ? idx : maxIdx,
            0
          );
          potentialProbabilityArray[maxIndex].percentage += discrepancy;
        }
      }

      // Convert percentages to fixed decimal places for display
      potentialProbabilityArray = potentialProbabilityArray.map((prob) => ({
        ...prob,
        percentage: Number(prob.percentage).toFixed(2),
      }));

      setCalculatedPercentage(totalPercentage.toFixed(2));
      setProbabilityStatus(potentialProbabilityArray);
      setTotalProbabilityAmount(totalProbAmount);
      setTotalProbabilityCount(totalCount);
    },
    [
      setProbabilityStatus,
      setTotalProbabilityAmount,
      setTotalProbabilityCount,
      filteredLeadReportsLead,
    ]
  );

  useEffect(() => {
    if (filteredLeadReportsLead) {
      const pendingLeads = filteredLeadReportsLead.filter(
        (lead) => lead.Status === "Pending"
      );

      const sortedLeads = pendingLeads.sort(
        (a, b) => new Date(a.LeadDate) - new Date(b.LeadDate)
      );

      aggregateProbabilityData(sortedLeads);
      setProbabilityDownload(sortedLeads);
    }
  }, [filteredLeadReportsLead, aggregateProbabilityData]);

  const navigate = useNavigate();
  const probPieChart = useRef();
  const probChartRef = useRef(null);

  useEffect(() => {
    dispatch(fetchGroups());
  }, [dispatch]);

  const probabilityNavigationl1 = useCallback(
    (clickedProb) => {
      const filteredDataprobabilityl1 = probabilityDownload.filter(
        (item) => item.Probability === clickedProb
      );
      dispatch(setFilteredNavData(filteredDataprobabilityl1));
      navigate(`/dashboard/lead-report`);
    },
    [probabilityDownload, navigate, dispatch]
  );

  const handleSubmit = () => {
    setAssignedGroup(selectedGroup); // Update AssignedGroup on submit
    setProbabilityFilterDateFrom(selectedFromDate);
    setProbabilityFilterDateTo(selectedToDate);
  };

  const handleFilter = useCallback(() => {
    // Run the filtering logic based on AssignedGroup (not selectedGroup)
    const filteredData = filteredLeadReportsLead.filter((LeadReport) => {
      const reportDate = new Date(LeadReport.LeadDate);
      const isWithinDateRange =
        (!probabilityFilterDateFrom ||
          reportDate >= probabilityFilterDateFrom) &&
        (!probabilityFilterDateTo || reportDate <= probabilityFilterDateTo);

      const isWithinGroup =
        !AssignedGroup ||
        AssignedGroup === "All" ||
        getLeadGroupId(LeadReport) === AssignedGroup ||
        getLeadGroupName(LeadReport) === AssignedGroup;

      return isWithinDateRange && isWithinGroup;
    });

    aggregateProbabilityData(filteredData);
    setProbabilityDownload(filteredData);
  }, [
    filteredLeadReportsLead,
    probabilityFilterDateFrom,
    probabilityFilterDateTo,
    AssignedGroup,
    aggregateProbabilityData,
    getLeadGroupId,
    getLeadGroupName,
  ]);

  useEffect(() => {
    handleFilter(); // Automatically runs when AssignedGroup updates
  }, [handleFilter]);

  useEffect(() => {
    // Call handleFilter whenever AssignedGroup is updated after submit
    if (AssignedGroup || probabilityFilterDateFrom || probabilityFilterDateTo) {
      handleFilter();
    }
  }, [
    AssignedGroup,
    probabilityFilterDateFrom,
    probabilityFilterDateTo,
    handleFilter,
  ]);

  useEffect(() => {
    if (probabilityStatus.length > 0 && probabilityTab === "graph") {
      const ctxProb = probPieChart.current;

      if (probChartRef.current) {
        probChartRef.current.destroy();
      }

      probChartRef.current = new Chart(ctxProb, {
        type: "pie",
        data: {
          labels: probabilityStatus.map((item) => item.prob),
          datasets: [
            {
              label: "Amount",
              data: probabilityStatus.map((item) => item.count.toFixed(2)),
              backgroundColor: ["#15803d", "orange", "#DC2626"],
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              yAlign: "bottom", // Moves tooltip higher
              callbacks: {
                title: () => "",
                label: function (context) {
                  const label = context.label || "";
                  return `${label}`;
                },
              },
              displayColors: false,
              backgroundColor: "rgba(255, 255, 255, 0)", // Fully transparent background
              titleColor: "black",
              bodyColor: "black",
              borderWidth: 0,
              boxPadding: 0,
              bodyFont: {
                size: 14, // Font size
                family: "Arial", // Font family
                weight: "bold", // Font weight
              },
            },

            datalabels: {
              formatter: (value, context) => {
                const total = totalprobabilityCount;
                const percentage = ((value / total) * 100).toFixed(2);
                return `${percentage}%`; // Show percentage inside pie chart
              },
              color: "black", // Text color
              font: {
                size: 14, // Font size
                family: "Arial", // Font family
                weight: "bold", // Font weight
              },
            },
          },
          layout: {
            padding: {
              bottom: 10,
            },
          },
          cutout: "50%",
          onClick: (event, elements) => {
            if (elements.length > 0) {
              const index = elements[0].index;
              const clickedItem = probabilityStatus[index];
              probabilityNavigationl1(clickedItem.prob);
            }
          },
        },
        plugins: [ChartDataLabels], // Enable ChartDataLabels plugin
      });
    }

    return () => {
      if (probChartRef.current) {
        probChartRef.current.destroy();
      }
    };
  }, [
    probabilityStatus,
    totalprobabilityCount,
    probabilityTab,
    probabilityNavigationl1,
    showgraph
  ]);

  return (
      <div className="rounded-lg w-full h-full justify-center font-semibold"
        Style={{ fontFamily: "Lora", fontWeight: 600 }}>
        <div className="min-h-[10%] px-3 rounded-md bg-white">
          <div className="flex flex-wrap justify-start items-center xl:flex xl:justify-between md:justify-between md:flex-nowrap ">
            <div className="flex gap-2 pb-2">
              <h1 className="text-2xl font-semibold sm:text-sm md:text-sm lg:text-xl ">
                PROBABILITY STATUS
              </h1>

            </div>

            <div className="md:flex lg:ml-8 lg:flex lg:items-center lg:justify-end xl:flex xl:justify-end ">
              <div className="show-potential-download lg:justify-end ">
                <div className=" inline-block relative px-3 mt-2">
                  <div ref={popupRef} className={`flex items-center text-4xl sm:text-md md:text-xl lg:text-xl xl:text-2xl 2xl:text-4xl ${probabilityStatus.length === 0 ? 'opacity-50 pointer-events-none' : ''}`}>
                    <CloudDownloadIcon
                      onClick={toggleDropdown}
                      className="cursor-pointer"
                    // fontSize="4rem"
                    />

                    {isDropdownVisible && (
                      <div

                        className="absolute top-8 -left-0 shadow-lg z-50 bg-white text-base"
                      >
                        <div
                          className="px-2 py-2 cursor-pointer hover:bg-gray-100"
                          onClick={() =>
                            handleDownload({ target: { value: "pdf" } })
                          }
                        >
                          PDF
                        </div>
                        <div
                          className="px-2 py-2 cursor-pointer hover:bg-gray-100"
                          onClick={() =>
                            handleDownload({ target: { value: "excel" } })
                          }
                        >
                          Excel
                        </div>
                        <div
                          className="px-2 py-2 cursor-pointer hover:bg-gray-100"
                          onClick={() =>
                            handleDownload({ target: { value: "word" } })
                          }
                        >
                          Word
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="show-potential-download lg:justify-end">
                <div className=" flex items-center justify-between text-3xl sm:text-xl md:text-md">
                  {probabilityTab === "table" && (
                    <span title="Graph">
                      <SignalCellularAltIcon
                        className="cursor-pointer"
                        onClick={() => setProbabilityTab("graph")}
                        // fontSize="3rem"
                      />
                    </span>
                  )}
                  {probabilityTab === "graph" && (
                    <span title="Table">
                      <TableChartIcon
                        className="cursor-pointer"
                        onClick={() => setProbabilityTab("table")}
                        // fontSize="3rem"
                      />
                    </span>
                  )}
                </div>
              </div>
                            <div onClick={() => handleToggleTable()} className=" ml-4 mt-1 flex justify-center align-middle items-center border rounded-md">
                <RemoveIcon
                  className=" cursor-pointer"
                />
              </div>
            </div>
          </div>
                  <hr />
        <hr />
        </div>

        {showgraph && (
          <div className=" h-[23.5rem]">
            <div className=" bg-white flex w-full gap-3 px-3 items-center justify-between pt-1 lg:flex-nowrap ">
              <div className="flex justify-between align-middle items-center w-full gap-1">
                <div className="w-[25%] flex align-middle p-1 border border-[#e3eaff] rounded-md ">
                  <div className="pl-1 pt-1.5 text-xs ">
                    <p>From-</p>
                  </div>
                  <div className="h-full w-full ">
                    <DatePicker
                      selected={selectedFromDate}
                      onChange={handleProbabilityFromDate}
                      dateFormat={getDateFormat()}
                      className="w-full h-full text-xs outline-none date-picker-placeholder"
                      placeholderText={getDatePlaceholder()}
                      required
                    />
                  </div>
                </div>
                |
                <div className="w-[25%] flex align-middle  p-1 border border-[#e3eaff] rounded-md ">
                  <div className="pl-1 pt-1.5 text-xs ">
                    <p>To-</p>
                  </div>
                  <div className="h-full w-full">
                    <DatePicker
                      selected={selectedToDate}
                      onChange={handleProbabilityToDate}
                      dateFormat={getDateFormat()}
                      className="w-full h-full text-xs outline-none date-picker-placeholder"
                      placeholderText={getDatePlaceholder()}
                      required
                      minDate={
                        probabilityFilterDateFrom
                          ? new Date(probabilityFilterDateFrom.getTime() + 86400000)
                          : null
                      }
                    />
                  </div>
                </div>
                <div className="relative flex align-middle items-center w-[25%]">
                  <div className="absolute cursor-pointer right-0 border-r border-[#e3eaff] bg-white z-20">
                    <ExpandMoreIcon
                      color="white"
                      className=""
                      onClick={() => setShowGroupDropdown(!showGroupDropdown)}
                    />
                  </div>
                  <div
                    ref={dropdownRefGroup}
                    className="relative border w-full border-[#e3eaff]  rounded-md"
                  >
                    <button
                      type="button"
                      onClick={() => setShowGroupDropdown(!showGroupDropdown)}
                      className=" flex justify-start items-center align-middle w-full h-full p-2 text-nowrap truncate focus:outline-none text-xs"
                    >
                      {probabilityGroupDropdownLabel}
                    </button>

                    {showGroupDropdown && (
                      <div className="absolute font-semibold flex flex-col w-28 p-0.5 gap-2 z-50  bg-white text-xs border border-gray-300 overflow-y-auto shadow-lg max-h-48  mt-0.5">
                        <label
                          htmlFor="All"
                          className="p-1 hover:bg-gray-300 font-semibold"
                          onClick={() => {
                            handleProbabilityGroup("All");
                          }}
                        >
                          All
                        </label>

                        {/* Show Selected Group from Redux at the Top */}
                        {groupFilterRedux && groupFilterRedux !== "All" && (
                          <div
                            className="p-1 bg-gray-300 font-semibold cursor-pointer text-xs"
                            onClick={() => handleProbabilityGroup(groupIdByName.get(String(groupFilterRedux).trim()) || groupFilterRedux)}
                          >
                            {groupNameById.get(groupIdByName.get(String(groupFilterRedux).trim()) || groupFilterRedux) || groupFilterRedux}
                          </div>
                        )}

                        {(Array.isArray(group) ? group : []).filter((groupItem) => {
                          const groupName = String(groupItem?.name ?? groupItem?.newGroupName ?? groupItem?.groupName ?? "").trim();
                          return groupName !== String(groupFilterRedux || "").trim() && groupName !== "All";
                        }).map((groupItem) => (
                          <div
                            className="p-1 hover:bg-gray-200 cursor-pointer"
                            onClick={() =>
                              handleProbabilityGroup(String(groupItem?._id ?? groupItem?.id ?? ""))
                            }
                            key={groupItem?._id ?? groupItem?.id}
                            value={groupItem?._id ?? groupItem?.id}
                          >
                            {groupItem?.name ?? groupItem?.newGroupName ?? groupItem?.groupName}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="w-[25%] flex justify-end ">
                  <button
                    className="text-white bg-[#e32278] p-2 px-4 text-xs rounded-md max-w-24 shadow-md"
                    onClick={handleSubmit}
                  >
                    SUBMIT
                  </button>
                </div>
              </div>
            </div>
            <div className="h-[21rem] flex bg-white px-3 pb-2 rounded-b-md">
              {probabilityTab === "table" ? (
                probabilityStatus.length > 0 ? (
                  <div className="w-full pt-3">
                    <div className="overflow-auto max-h-[18rem]">
                      <table className="w-full divide-y divide-gray-200">
                        <thead className="bg-black sticky top-0">
                          <tr className="text-white">
                            <th className="px-3 py-2 text-left border text-sm font-medium capitalize tracking-wider">
                              Probability
                            </th>
                            <th className="px-3 py-2 text-center border text-sm font-medium capitalize tracking-wider">
                              Count
                            </th>
                            <th className="px-3 py-2 text-center border text-sm font-medium capitalize tracking-wider">
                              %
                            </th>
                            <th className="px-3 py-2 text-right border text-sm font-medium capitalize tracking-wider">
                              Amount
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {probabilityStatus
                            .sort((a, b) => {
                              const probOrder = ["HIGH", "MEDIUM", "LOW"];
                              return (
                                probOrder.indexOf(a.prob.toUpperCase()) -
                                probOrder.indexOf(b.prob.toUpperCase())
                              );
                            })
                            .map((item, index) => (
                              <tr className="border text-[16px]" key={index}>
                                <td
                                  onClick={() => probabilityNavigationl1(item.prob)}
                                  style={{ cursor: "pointer" }}
                                  className="px-2 text-left py-1 font-thin"
                                >
                                  {item.prob.toUpperCase()}
                                </td>
                                <td className="px-2 text-center py-1">
                                  {item.count}
                                </td>
                                <td className="px-2 text-center py-1">
                                  {item.percentage} %
                                </td>
                                <td className="px-2 text-right py-1">
                                  {currency}
                                  {USAComma(item.amount)}
                                </td>
                              </tr>
                            ))}
                          {/* Display total row */}
                          <tr className="border bg-gray-200 font-bold text-[16px]">
                            <td className="px-3 text-left py-2">TOTAL :</td>
                            {/* <td className="px-3 text-center py-2">{totalprobabilityCount}</td> */}
                            <td className="px-3 text-center py-2">
                              {probCalculatedCount}
                            </td>

                            <td className="px-3 text-center py-2">
                              {calculatedPercentage === "99.99"
                                ? 100
                                : calculatedPercentage}
                              %
                            </td>

                            <td className="px-3 text-right py-2">
                              {currency}
                              {USAComma(totalProbabilityAmount)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-center align-middle items-center text-center mb-5  w-full">
                    <p className="noDataAvailable">
                      No data available
                    </p> </div>
                )
              ) : probabilityStatus.length > 0 ? (
                <div className="h-[90%] w-full p-9 ">
                  <canvas id="probPieChart" width="60%" ref={probPieChart}></canvas>
                </div>
              ) : (
                <div className="flex justify-center align-middle items-center text-center mb-5 w-full">
                  <p className="noDataAvailable">
                    No data available
                  </p> </div>
              )}
            </div>
          </div>
        )}
      </div>
  );
};

export default ProbabilityStatusL1;
